import { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';

const MAX_PARTICLES = 420;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const resolveNodeColor = (node, aiMode) => {
  if (node?.processing) return '#facc15';
  const status = String(node?.status || 'unknown').toLowerCase();
  if (status === 'critical' || status === 'offline') return '#ef4444';
  if (status === 'warning') return '#f59e0b';
  if (status === 'online' || status === 'normal') return '#22c55e';
  if (aiMode && node?.aiHighlight) return '#22d3ee';
  return '#94a3b8';
};

const resolveLinkColor = (link) => {
  const status = String(link?.status || 'normal').toLowerCase();
  if (link?.processing) return '#facc15';
  if (status === 'critical' || status === 'offline' || status === 'error') return '#ef4444';
  if (status === 'warning' || status === 'high') return '#f59e0b';
  return '#22d3ee';
};

const resolveLinkWeight = (loadPercent, status) => {
  const load = clamp(Number(loadPercent) || 0, 0, 100);
  let base = 1.4;
  if (load >= 70) base = 3.8;
  else if (load >= 30) base = 2.6;
  const normalizedStatus = String(status || '').toLowerCase();
  if (normalizedStatus === 'critical' || normalizedStatus === 'offline' || normalizedStatus === 'error') return base + 0.8;
  if (normalizedStatus === 'warning' || normalizedStatus === 'high') return base + 0.4;
  return base;
};

const samplePolylinePoint = (points, t) => {
  if (!Array.isArray(points) || points.length < 2) return null;
  const normalized = clamp(t, 0, 1);
  if (points.length === 2) {
    const [p0, p1] = points;
    return {
      x: p0.x + ((p1.x - p0.x) * normalized),
      y: p0.y + ((p1.y - p0.y) * normalized),
    };
  }
  const segments = [];
  let totalLength = 0;
  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[index];
    const p1 = points[index + 1];
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const length = Math.hypot(dx, dy);
    segments.push({ p0, p1, length });
    totalLength += length;
  }
  if (totalLength <= 0) return points[0];
  let target = normalized * totalLength;
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    if (target <= segment.length || index === segments.length - 1) {
      const lt = segment.length <= 0 ? 0 : target / segment.length;
      return {
        x: segment.p0.x + ((segment.p1.x - segment.p0.x) * lt),
        y: segment.p0.y + ((segment.p1.y - segment.p0.y) * lt),
      };
    }
    target -= segment.length;
  }
  return points[points.length - 1];
};

const resolveArrowRotation = (points, t) => {
  const a = samplePolylinePoint(points, clamp(t - 0.015, 0, 1));
  const b = samplePolylinePoint(points, clamp(t + 0.015, 0, 1));
  if (!a || !b) return 0;
  return Math.atan2(b.y - a.y, b.x - a.x);
};

const drawArrow = (ctx, point, rotation, color, size = 6, alpha = 0.95) => {
  if (!point) return;
  ctx.save();
  ctx.translate(point.x, point.y);
  ctx.rotate(rotation);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(size, 0);
  ctx.lineTo(-size * 0.55, size * 0.45);
  ctx.lineTo(-size * 0.55, -size * 0.45);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

const buildParticles = (links = []) => {
  const particles = [];
  links.forEach((link, index) => {
    const load = clamp(Number(link?.load || 0), 0, 100);
    const density = load >= 75 ? 8 : load >= 45 ? 5 : load >= 20 ? 3 : 2;
    const direction = String(link?.direction || 'downstream').toLowerCase() === 'upstream' ? -1 : 1;
    const status = String(link?.status || 'normal').toLowerCase();
    const latency = clamp(Number(link?.latencySeconds || 10), 1, 240);
    const speedBase = clamp(0.08 + ((240 - latency) / 240) * 0.18, 0.06, 0.28);
    const speed = speedBase + (status === 'critical' || status === 'offline' ? 0.03 : 0);
    const color = resolveLinkColor(link);
    for (let i = 0; i < density; i += 1) {
      if (particles.length >= MAX_PARTICLES) break;
      particles.push({
        id: `${link?.id || index}-${i}`,
        linkIndex: index,
        progress: Math.random(),
        direction,
        speed,
        color,
        radius: load >= 70 ? 2.3 : 1.7,
        alpha: status === 'critical' || status === 'offline' ? 0.95 : 0.8,
      });
    }
  });
  return particles;
};

const LeafletRealtimeNetworkLayer = ({
  enabled = true,
  animate = true,
  aiMode = false,
  nodes = [],
  links = [],
  clusters = [],
  focusedNodeId = '',
}) => {
  const map = useMap();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(performance.now());
  const dataRef = useRef({ nodes: [], links: [], clusters: [] });
  const particlesRef = useRef([]);

  const overlayData = useMemo(() => ({
    nodes: Array.isArray(nodes) ? nodes : [],
    links: Array.isArray(links) ? links : [],
    clusters: Array.isArray(clusters) ? clusters : [],
  }), [nodes, links, clusters]);

  useEffect(() => {
    dataRef.current = overlayData;
    particlesRef.current = buildParticles(overlayData.links);
  }, [overlayData]);

  useEffect(() => {
    if (!enabled || !map) return undefined;
    const container = map.getContainer();
    if (!container) return undefined;

    const canvas = document.createElement('canvas');
    canvas.className = 'ktc-map-realtime-canvas';
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.zIndex = '460';
    canvas.style.pointerEvents = 'none';
    container.appendChild(canvas);
    canvasRef.current = canvas;

    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = container.clientWidth;
      const height = container.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * ratio));
      canvas.height = Math.max(1, Math.floor(height * ratio));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    resizeCanvas();
    map.on('resize move zoom', resizeCanvas);
    return () => {
      map.off('resize move zoom', resizeCanvas);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      canvas.remove();
      canvasRef.current = null;
    };
  }, [enabled, map]);

  useEffect(() => {
    if (!enabled || !canvasRef.current || !map) return undefined;

    const drawFrame = (timestamp) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const delta = Math.min(0.05, (timestamp - lastTimeRef.current) / 1000);
      lastTimeRef.current = timestamp;

      ctx.clearRect(0, 0, width, height);
      const bounds = map.getBounds();
      const { nodes: drawNodes, links: drawLinks, clusters: drawClusters } = dataRef.current;
      const focus = String(focusedNodeId || '').trim();
      const visibleLinks = [];

      drawLinks.forEach((link) => {
        const points = (Array.isArray(link?.points) ? link.points : [])
          .map((point) => {
            const lat = Number(point?.[0]);
            const lng = Number(point?.[1]);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
            if (!bounds.pad(0.2).contains([lat, lng])) return null;
            const projected = map.latLngToContainerPoint([lat, lng]);
            return { x: projected.x, y: projected.y };
          })
          .filter(Boolean);
        if (points.length < 2) return;

        const related = Boolean(link?.relatedToFocus || !focus);
        const alpha = related ? 0.94 : 0.14;
        const color = resolveLinkColor(link);
        const weight = resolveLinkWeight(link?.load, link?.status);
        const status = String(link?.status || 'normal').toLowerCase();

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        points.forEach((point, index) => {
          if (index === 0) ctx.moveTo(point.x, point.y);
          else ctx.lineTo(point.x, point.y);
        });
        ctx.strokeStyle = color;
        ctx.lineWidth = weight;
        ctx.lineCap = 'round';
        if (String(link?.direction || '').toLowerCase() === 'upstream') {
          ctx.setLineDash([7, 8]);
        } else {
          ctx.setLineDash([]);
        }
        const offsetBase = (timestamp / 14) % 1000;
        ctx.lineDashOffset = animate ? (String(link?.direction || '').toLowerCase() === 'upstream' ? offsetBase : -offsetBase) : 0;
        if (Number(link?.load || 0) >= 70 || status === 'critical' || status === 'offline') {
          ctx.shadowBlur = status === 'critical' || status === 'offline' ? 12 : 8;
          ctx.shadowColor = color;
        }
        ctx.stroke();
        ctx.restore();

        const arrowT = String(link?.direction || '').toLowerCase() === 'upstream' ? 0.35 : 0.72;
        const arrowPoint = samplePolylinePoint(points, arrowT);
        const arrowRotation = resolveArrowRotation(points, arrowT);
        drawArrow(ctx, arrowPoint, arrowRotation, color, status === 'critical' || status === 'offline' ? 7 : 6, alpha);

        if (aiMode && link?.aiHighlight) {
          ctx.save();
          ctx.globalAlpha = related ? 0.72 : 0.18;
          ctx.beginPath();
          points.forEach((point, index) => {
            if (index === 0) ctx.moveTo(point.x, point.y);
            else ctx.lineTo(point.x, point.y);
          });
          ctx.strokeStyle = '#22d3ee';
          ctx.lineWidth = weight + 1.4;
          ctx.shadowBlur = 12;
          ctx.shadowColor = '#22d3ee';
          ctx.stroke();
          ctx.restore();
        }

        visibleLinks.push({ id: link?.id, points, related });
      });

      if (animate) {
        particlesRef.current.forEach((particle) => {
          const target = visibleLinks.find((item) => item.id === drawLinks[particle.linkIndex]?.id);
          if (!target || !target.points || target.points.length < 2) return;
          particle.progress += delta * particle.speed * (particle.direction > 0 ? 1 : -1);
          if (particle.progress > 1) particle.progress = 0;
          if (particle.progress < 0) particle.progress = 1;
          const point = samplePolylinePoint(target.points, particle.progress);
          if (!point) return;
          ctx.save();
          ctx.globalAlpha = target.related ? particle.alpha : particle.alpha * 0.25;
          ctx.beginPath();
          ctx.fillStyle = particle.color;
          ctx.shadowColor = particle.color;
          ctx.shadowBlur = 8;
          ctx.arc(point.x, point.y, particle.radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        });
      }

      drawClusters.forEach((cluster) => {
        const lat = Number(cluster?.latitude);
        const lng = Number(cluster?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (!bounds.pad(0.1).contains([lat, lng])) return;
        const point = map.latLngToContainerPoint([lat, lng]);
        const severity = clamp(Number(cluster?.severity || cluster?.count || 1), 1, 100);
        const radius = 18 + Math.min(38, severity * 1.2);
        const opacity = clamp(0.12 + (severity / 140), 0.12, 0.34);
        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.fillStyle = '#ef4444';
        ctx.strokeStyle = '#ef4444';
        ctx.lineWidth = 1.4;
        ctx.shadowBlur = 10;
        ctx.shadowColor = '#ef4444';
        ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
        ctx.save();
        ctx.font = '600 11px Inter, system-ui, sans-serif';
        ctx.fillStyle = 'rgba(255, 228, 230, 0.95)';
        ctx.textAlign = 'center';
        ctx.fillText(String(cluster?.label || ''), point.x, point.y - radius - 8);
        ctx.restore();
      });

      drawNodes.forEach((node) => {
        const lat = Number(node?.latitude);
        const lng = Number(node?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (!bounds.pad(0.15).contains([lat, lng])) return;
        const point = map.latLngToContainerPoint([lat, lng]);
        const type = String(node?.type || '').toLowerCase();
        const color = resolveNodeColor(node, aiMode);
        const isCritical = String(node?.status || '').toLowerCase() === 'critical' || String(node?.status || '').toLowerCase() === 'offline';
        const pulse = 1 + ((Math.sin(timestamp / 220) + 1) * 0.08);
        const baseSize = type === 'olt' ? 8 : type === 'odp' ? 6.6 : 4.8;
        const size = (isCritical ? baseSize + 1.5 : baseSize) * pulse;
        const related = Boolean(node?.relatedToFocus || !focus);
        const alpha = related ? 0.96 : 0.18;

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.shadowColor = color;
        ctx.shadowBlur = isCritical ? 16 : 8;
        ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        if (isCritical || node?.processing || (aiMode && node?.aiHighlight)) {
          ctx.save();
          ctx.globalAlpha = related ? 0.55 : 0.14;
          ctx.beginPath();
          ctx.strokeStyle = node?.processing ? '#facc15' : (isCritical ? '#ef4444' : '#22d3ee');
          ctx.lineWidth = 1.4;
          ctx.arc(point.x, point.y, size + 7 + ((Math.sin(timestamp / 180) + 1) * 2.2), 0, Math.PI * 2);
          ctx.stroke();
          ctx.restore();
        }

        if (aiMode && node?.aiHighlight) {
          ctx.save();
          ctx.globalAlpha = related ? 0.95 : 0.3;
          ctx.fillStyle = '#22d3ee';
          ctx.font = '700 9px Inter, system-ui, sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('AI', point.x, point.y - (size + 10));
          ctx.restore();
        }
      });

      rafRef.current = requestAnimationFrame(drawFrame);
    };

    rafRef.current = requestAnimationFrame(drawFrame);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [enabled, animate, aiMode, map, focusedNodeId]);

  return null;
};

export default LeafletRealtimeNetworkLayer;

